import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
} from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subscription, take, tap } from 'rxjs';
import { getAiServiceLogoPath } from 'src/app/common/aiServiceUtils';
import { getResourceCategoryIconPath } from 'src/app/common/categoryUtils';
import { Stack } from 'src/app/common/dataStructures/stack';
import { formatFromDate } from 'src/app/common/datetime_utility';
import { getPlatformLogoPath } from 'src/app/common/platformUtils';
import { StatusLabelEnum } from 'src/app/components/status-label/status-label.component';
import { RowActionClicked } from 'src/app/components/table-row/table-row.component';
import { PaginationOperationEnum } from 'src/app/components/table/table.component';
import { ResourceType } from 'src/app/models/api';
import {
  InventoryConnectionResourceProperties,
  InventoryEntityFieldStatus,
  InventoryResourceModel,
} from 'src/app/models/api/inventoryResource.model';
import { JsonHalCollection, JsonHalCollectionPageInfo, JsonHalLinks } from 'src/app/models/api/jsonhal.model';
import { PermissionEnum } from 'src/app/models/auth0/permission.enum';
import { ITablePageInfo, jsonHalPageInfoToTablePageInfo } from 'src/app/models/componentInputs/tablePageInfo.interface';
import { FeatureFlagTypes } from 'src/app/models/featureFlag/featureFlagsTypes.enum';
import { InventoryFiltersModel } from 'src/app/models/filters/implementations/pageFilters/inventoryFilters.model';
import { PageWithFiltersEnum } from 'src/app/models/filters/pageWithFilters.enum';
import { ISelectedFilters } from 'src/app/models/filters/selectedFilters.interface';
import { IPaginationNavigationModel } from 'src/app/models/paginationNavigation.model';
import { ISelectOption } from 'src/app/models/selectOptions';
import { SortColumn } from 'src/app/models/sortColumn.model';
import { ITableColumn } from 'src/app/models/tableColumns';
import {
  InventoryTableColumns,
  PrivilegedTableColumn,
  ManagedTableColumn,
} from 'src/app/models/tableColumns/inventoryTableColumn.model';
import { SupportedSeverityLabels } from 'src/app/models/tableColumns/supportedSeverityLabels.enum';
import { IInventoryResourceViewModel } from 'src/app/models/viewModels/inventoryResourceViewModel';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { InventoryActionsService } from 'src/app/services/actions/inventory/inventory-actions.service';
import { InventorySupportedActions } from 'src/app/services/actions/inventory/inventorySupportedActions';
import { FiltersService } from 'src/app/services/filters/filters.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { InventoryHttpService } from 'src/app/services/httpServices/inventoryHttpService/inventory-http.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';
import { FieldName, TextProviderService } from 'src/app/services/infra/textProviderService/text-provider.service';
import { InventoryHelper } from './helpers/inventoryHelper';
import { populateErrorMessage } from '../utils/utils';
import { DisplayNameService } from 'src/app/services/common/displayName/displayName.service';
import { buildResourceTypeDisplayName } from '../../labels/utils';
import { ColumnsOrderService } from 'src/app/services/common/columnsOrder/columns-order.service';
import { TableColumnsNames } from 'src/app/models/tableColumns/tableColumnsNames.enum';
import { ActiveFiltersHeightService } from 'src/app/services/infra/activeFiltersHeight/active-filters-height.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccountCategory } from 'src/app/models/api/accountManagement.model';
import { AccountService } from 'src/app/services/infra/account/account.service';
import { SidePanelService } from '../../features/side-panel/services/side-panel.service';
import { AccessService } from 'src/app/services/infra/access/access.service';
import { FeatureFlagsService } from 'src/app/services/infra/featureFlags/feature-flags.service';
import { RSA_DEMO_AI_SERVICES } from 'src/app/models/selectOptions/rsaDemoAiServices';
import { copilotToAgentTypeMapping } from 'src/app/common/vertical-filters-utils';

@Component({
  selector: 'app-inventory',
  templateUrl: './inventory.component.html',
  styleUrls: ['./inventory.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InventoryComponent implements OnInit, OnDestroy {
  public columns = signal<ITableColumn[]>([]);
  public pageName = TableColumnsNames.Inventory;
  public inventoryResourcesCurrentPage$: BehaviorSubject<IInventoryResourceViewModel[]> = new BehaviorSubject<
    IInventoryResourceViewModel[]
  >([]);
  public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public showNoData = false;
  public isFiltersMenuOpen = false;

  public readonly defaultPageSize: number;
  public pageFilters: InventoryFiltersModel;
  public optionsAreSelected = false;
  public pageInfo$: BehaviorSubject<ITablePageInfo>;
  public totalItems = 0;
  public resourcesCount: BehaviorSubject<number> = new BehaviorSubject(-1);
  public featureFlagTypes = FeatureFlagTypes;

  public accountCategory$: Observable<AccountCategory | null>;

  public permissionsEnum = PermissionEnum;
  public AccountCategory = AccountCategory;

  public accessService = inject(AccessService);

  private _currentInventoryResourcesById: {
    [inventoryResourceId: string]: InventoryResourceModel;
  } = {};
  private readonly _notAvailableString: string;

  private _paginationNavigation: IPaginationNavigationModel;

  private _filtersOnChangeSubscription$: Subscription;
  private _firstPageSubscription$: Subscription;
  private _fetchByPageSubscription$: Subscription;
  private _sortOnChangeSubscription$: Subscription;

  public _accountService = inject(AccountService);

  private readonly _httpClient = inject(HttpClient);
  private readonly _featureFlagsService = inject(FeatureFlagsService);
  private readonly _rsaDemoServiceSet = new Set([...RSA_DEMO_AI_SERVICES, 'langchain']);
  private _isOnFirstPage = true;
  private _rsaMockResourcesRaw: InventoryResourceModel[] | null = null;
  private _rsaMockCount = 0;
  private _iamDemoMockResourcesRaw: InventoryResourceModel[] | null = null;
  private _iamDemoMockCount = 0;

  constructor(
    private readonly _sidePanel: SidePanelService,
    private readonly _logger: LoggerService,
    private readonly _inventoryHttpService: InventoryHttpService,
    private readonly _filtersService: FiltersService,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _inventoryActionsService: InventoryActionsService,
    private readonly _textProviderService: TextProviderService,
    private readonly _displayNameService: DisplayNameService,
    private readonly _columnsOrderService: ColumnsOrderService,
    private readonly _activeFiltersHeightService: ActiveFiltersHeightService,
    private readonly _renderer: Renderer2,
    private readonly _el: ElementRef,
    private readonly _destroyRef: DestroyRef
  ) {
    this.defaultPageSize = this._inventoryHttpService.defaultPageSize;
    this._notAvailableString = this._labelsProviderPipe.transform('common.notAvailable');
    this._initializePageInfo();
  }

  ngOnInit(): void {
    this.accountCategory$ = this._accountService.getAccountCategory$();

    const baseColumns = this._isIamDemoEnabled()
      ? [...InventoryTableColumns, PrivilegedTableColumn, ManagedTableColumn]
      : InventoryTableColumns;
    this.columns.set(this._columnsOrderService.getColumnsOrderFromLocalStorage(this.pageName, baseColumns));

    this.pageFilters = this._filtersService.getFilters(PageWithFiltersEnum.Inventory) as InventoryFiltersModel;
    this.pageFilters.restoreSortColumnToDefaultIfNeeded();

    this._subscribeToHandleFiltersOnChange();
    this._subscribeToSortOnChange();

    this._activeFiltersHeightService.height$
      .pipe(
        tap((height: number) =>
          this._renderer.setStyle(this._el.nativeElement, '--applied-filters-height', `${height}px`, 1)
        ),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this._filtersOnChangeSubscription$.unsubscribe();
    this._sortOnChangeSubscription$.unsubscribe();
  }

  public tablesFiltersChange(selectedOptions: ISelectOption[]) {
    this.pageFilters.updateFilterFromUserSelection(undefined, selectedOptions);
  }

  public onPagination(operation: PaginationOperationEnum) {
    if (!this.isLoading$.value) {
      if (operation == PaginationOperationEnum.next) {
        this._paginationNavigation.previousPage.push(this._paginationNavigation.currentPage);
      }

      const pageUrl =
        operation == PaginationOperationEnum.next
          ? this._paginationNavigation.nextPage
          : this._paginationNavigation.previousPage.pop();

      this._isOnFirstPage =
        operation === PaginationOperationEnum.prev && this._paginationNavigation.previousPage.size() === 0;

      if (pageUrl) {
        this._doPaginationWhenTokenExist(pageUrl);
      }
    }
  }

  public filtersOnMenuStateChange(menuState: boolean) {
    this.isFiltersMenuOpen = menuState;
  }

  public rowSelected(selection: IInventoryResourceViewModel): void {
    this._sidePanel.openResourceDetails(selection);
  }

  public rowUnselected = () => this._sidePanel.close();

  public actionOnClick(actionClicked: RowActionClicked): void {
    const inventoryResourceViewModel = actionClicked.row.getValue() as IInventoryResourceViewModel;

    this._inventoryActionsService
      .handleActionClicked(
        actionClicked.action.name as InventorySupportedActions,
        this._currentInventoryResourcesById[inventoryResourceViewModel.id]
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();
  }

  public columnsSelected(selectedColumns: ITableColumn[]) {
    this.columns.set(structuredClone(selectedColumns));
  }

  private _subscribeToHandleFiltersOnChange() {
    this._filtersOnChangeSubscription$ = this.pageFilters.onUpdate$.subscribe((selectedFilters) => {
      this.resourcesCount.next(-1);
      this._rsaMockCount = 0;
      this._iamDemoMockCount = 0;
      this.optionsAreSelected = this.pageFilters.getAllSelectedOptions().length > 0;
      this._fetchFirstPage(selectedFilters, this.pageFilters.sortedColumn$.value);
    });
  }

  private _subscribeToSortOnChange() {
    this._sortOnChangeSubscription$ = this.pageFilters.sortedColumn$.subscribe((newSort) => {
      this._fetchFirstPage(this.pageFilters.getSelectedFilters(), newSort);
    });
  }

  private _fetchByPage(continuationToken: string) {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();

    this._fetchByPageSubscription$ = this._inventoryHttpService
      .getInventoryResourcesByContinuationToken(continuationToken)
      .subscribe(
        (response) => this._handleInventoryCollectionResponse(response),
        (error) => this._fetchRequestOnError(error)
      );
  }

  private _fetchFirstPage(filterOptions: ISelectedFilters, sortData?: SortColumn) {
    this._cancelOngoingHttpRequest();
    this._tableBehaviorChangeBeforeFetch();
    this._isOnFirstPage = true;

    const rsaEnabled = this._isRsaDemoEnabled();

    if (rsaEnabled && this._isOnlyRsaServicesSelected(filterOptions)) {
      this._firstPageSubscription$ = of(this._emptyResponse())
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          next: (r) => this._handleInventoryCollectionResponse(r),
        });
      return;
    }

    const stripped = rsaEnabled ? this._stripRsaServicesFromFilters(filterOptions) : filterOptions;

    this._firstPageSubscription$ = this._inventoryHttpService.startGetInventoryResources(stripped, sortData).subscribe({
      next: (response) => this._handleInventoryCollectionResponse(response),
      error: (err) => this._fetchRequestOnError(err),
    });
  }

  private _handleInventoryCollectionResponse(response: JsonHalCollection<InventoryResourceModel>): void {
    this._handlePaginationUrls(response._links);
    this._handlePageInfo(response.page);
    this.pageFilters.applyAiServiceFacetsIfProvided(response.facets);

    const items = response._embedded.items;
    this._currentInventoryResourcesById = {};

    const anyDemoEnabled = this._isRsaDemoEnabled() || this._isIamDemoEnabled();

    if (!items || items.length == 0) {
      if (anyDemoEnabled) {
        this._loadAllMockResources([]);
      } else {
        this.showNoData = true;
        this.inventoryResourcesCurrentPage$.next([]);
        this.isLoading$.next(false);
      }
    } else {
      this.showNoData = false;
      const viewModels = items.map((item) => this._transformResourceToViewModel(item));
      this._inventoryActionsService
        .setActionsForResources(items, viewModels)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe({
          complete: () => {
            if (anyDemoEnabled) {
              if (!response.facets || !response.facets['aiService']) {
                this.pageFilters.applyAiServiceFromItems(items);
              }
              this._loadAllMockResources(viewModels);
            } else {
              this.inventoryResourcesCurrentPage$.next(viewModels);
              if (!response.facets || !response.facets['aiService']) {
                this.pageFilters.applyAiServiceFromItems(items);
              }
              this.isLoading$.next(false);
            }
          },
        });
    }
  }

  private _cancelOngoingHttpRequest() {
    if (this._firstPageSubscription$) {
      this._firstPageSubscription$.unsubscribe();
    }

    if (this._fetchByPageSubscription$) {
      this._fetchByPageSubscription$.unsubscribe();
    }
  }

  private _fetchRequestOnError(error: HttpErrorResponse) {
    this.inventoryResourcesCurrentPage$.next([]);
    this.isLoading$.next(false);
    this.showNoData = true;
    this._httpErrorHandlerService.handle(error);
  }

  private _tableBehaviorChangeBeforeFetch() {
    this.inventoryResourcesCurrentPage$.next([]);
    this.isLoading$.next(true);
  }

  private _handlePaginationUrls(links: JsonHalLinks) {
    if (!this._paginationNavigation) {
      this._paginationNavigation = {
        currentPage: links.self.href,
        nextPage: links.next?.href,
        previousPage: new Stack<string>(),
      } as IPaginationNavigationModel;
    } else {
      this._paginationNavigation.currentPage = links.self.href;
      this._paginationNavigation.nextPage = links.next?.href;
    }
  }

  private _handlePageInfo(pageInfo: JsonHalCollectionPageInfo) {
    this.totalItems = pageInfo.totalItems + this._rsaMockCount + this._iamDemoMockCount;
    this.resourcesCount.next(this.totalItems);
    const tablePageInfo = jsonHalPageInfoToTablePageInfo(pageInfo, this.defaultPageSize);

    if (!this.pageInfo$) {
      this.pageInfo$ = new BehaviorSubject(tablePageInfo);
    } else {
      this.pageInfo$.next(tablePageInfo);
    }
  }

  private _transformResourceToViewModel(resource: InventoryResourceModel): IInventoryResourceViewModel {
    const securityRiskValue =
      resource.scores == null ? SupportedSeverityLabels.coming_soon : resource.scores.securityRisk.toString();
    const businessCriticality =
      resource.scores == null
        ? SupportedSeverityLabels.coming_soon
        : resource.scores.businessCriticality == null
          ? SupportedSeverityLabels.coming_soon
          : resource.scores.businessCriticality.toString();
    const errorMessage = populateErrorMessage(resource);
    const createdByDisplayName = this._displayNameService.getCreatedByDisplayName(
      resource.identities?.value?.createdBy,
      resource.additionalProperties
    );
    const ownerDisplayNames = resource.identities?.value?.ownedBy?.value
      ? resource.identities.value.ownedBy.value.map((owner) => owner.displayName).filter((displayName) => displayName)
      : [];
    this._currentInventoryResourcesById[resource.id] = resource;

    return {
      rowId: resource.id,
      id: resource.id,
      resourceName: resource.displayName,
      resourceType: this._getResourceTypeDisplay(resource),
      resourceTypeIcon: getResourceCategoryIconPath(resource.resourceCategory),
      toolTipResourceName: errorMessage != '' ? errorMessage : null,
      iconSrcResourceName: 'assets/images/status-icons/error.svg',
      platform: resource.platformInfo.platform,
      platformIcon: resource.aiService
        ? getAiServiceLogoPath(resource.aiService)
        : getPlatformLogoPath(resource.platformInfo.platform),
      environment: resource.environment?.value?.displayName ?? this._notAvailableString,
      notApplicableEnvTooltipText: this._textProviderService.getNotApplicableTableTooltipTextByResourceTypeIfNeeded(
        resource.resourceType,
        FieldName.environment,
        resource.environment?.value?.displayName
      ),
      createdBy: createdByDisplayName,
      notApplicableCreatedByTooltipText:
        this._textProviderService.getNotApplicableTableTooltipTextByResourceTypeIfNeeded(
          resource.resourceType,
          FieldName.createdBy,
          createdByDisplayName
        ),
      ownedBy: ownerDisplayNames,
      notApplicableOwnedByTooltipText: this._textProviderService.getNotApplicableTableTooltipTextByResourceTypeIfNeeded(
        resource.resourceType,
        FieldName.ownedBy,
        ownerDisplayNames[0]
      ),
      creationTime: resource.platformInfo.creationTime.value
        ? formatFromDate(resource.platformInfo.creationTime.value)
        : this._notAvailableString,
      businessCriticality: businessCriticality,
      securityRisk: securityRiskValue,
      statusLabel: resource.resourceState
        ? InventoryHelper.getResourceStatusLabel(resource.resourceState.status)
        : StatusLabelEnum.active,
      clickable: true,
      menuActions: [],
      resource: resource,
      privileged: resource.additionalProperties?.['privileged'] ? 'Yes' : 'No',
      managed: resource.additionalProperties?.['managed'] ? 'Yes' : 'No',
    } as IInventoryResourceViewModel;
  }

  private _getResourceTypeDisplay(resource: InventoryResourceModel): string {
    let resourceTypeDisplayName = buildResourceTypeDisplayName(resource.resourceType);

    if (resource.resourceType === ResourceType.connection) {
      const connector = (resource.resourceCategoryProperties as InventoryConnectionResourceProperties).connector;
      if (connector.status === InventoryEntityFieldStatus.hasValue) {
        resourceTypeDisplayName += ` (${connector.value?.displayName})`;
      }
    }

    return resourceTypeDisplayName;
  }

  private _doPaginationWhenTokenExist(url: string) {
    const continuationToken = this._tokenExtractor(url);

    if (continuationToken) {
      this._fetchByPage(continuationToken);
    }
  }

  private _tokenExtractor(url: string): string | null {
    const continuationToken = new URLSearchParams(url.slice(url.indexOf('?'))).get('continuationToken');
    if (!continuationToken) {
      this._logger.error('Failed to extract continuation token from json hal next inventory resource page link');
      return null;
    }

    return continuationToken;
  }

  private _initializePageInfo() {
    this.pageInfo$ = new BehaviorSubject({
      number: 1,
      currSize: 0,
      maxSize: this._inventoryHttpService.defaultPageSize,
      totalItems: 0,
      totalPages: 1,
    });
  }

  private _handleResourceDeleteActionResponse(actionResponse: Observable<boolean>) {
    actionResponse.subscribe((resp) => {
      if (resp) {
        this._refreshCurrentPage();
      }
    });
  }

  private _refreshCurrentPage() {
    const continuationToken = this._tokenExtractor(this._paginationNavigation.currentPage);
    if (continuationToken) {
      this._fetchByPage(continuationToken);
    }
  }

  private _isRsaDemoEnabled(): boolean {
    return this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.RsaDemoRuntimeBoundaries);
  }

  private _isIamDemoEnabled(): boolean {
    return this._featureFlagsService.isFeatureEnabled(FeatureFlagTypes.SpmIamDemoAmex);
  }

  private _emptyResponse(): JsonHalCollection<InventoryResourceModel> {
    return {
      _embedded: { items: [] },
      _links: { self: { href: '' } },
      page: {
        number: 1,
        size: this.defaultPageSize,
        totalItems: 0,
        totalPages: 1,
      },
    } as unknown as JsonHalCollection<InventoryResourceModel>;
  }

  private _stripRsaServicesFromFilters(filters: ISelectedFilters): ISelectedFilters {
    if (!filters.aiService?.length) return filters;
    return {
      ...filters,
      aiService: filters.aiService.filter((s) => !this._rsaDemoServiceSet.has(s)),
    };
  }

  private _isOnlyRsaServicesSelected(filters: ISelectedFilters): boolean {
    if (!filters.aiService?.length) return false;
    return filters.aiService.every((s) => this._rsaDemoServiceSet.has(s));
  }

  private _loadAllMockResources(apiViewModels: IInventoryResourceViewModel[]): void {
    if (!this._isOnFirstPage) {
      this.showNoData = apiViewModels.length === 0;
      this.inventoryResourcesCurrentPage$.next(apiViewModels);
      this.isLoading$.next(false);
      return;
    }

    const rsaNeeded = this._isRsaDemoEnabled();
    const iamDemoNeeded = this._isIamDemoEnabled();

    const rsaReady = !rsaNeeded || !!this._rsaMockResourcesRaw;
    const iamDemoReady = !iamDemoNeeded || !!this._iamDemoMockResourcesRaw;

    if (rsaReady && iamDemoReady) {
      this._prependAllMockResources(apiViewModels);
      return;
    }

    const loads: Observable<InventoryResourceModel[]>[] = [];

    if (rsaNeeded && !this._rsaMockResourcesRaw) {
      loads.push(
        this._httpClient.get<InventoryResourceModel[]>('assets/mockData/rsaInventoryMockData.json').pipe(
          take(1),
          tap((m) => (this._rsaMockResourcesRaw = m))
        )
      );
    }

    if (iamDemoNeeded && !this._iamDemoMockResourcesRaw) {
      loads.push(
        this._httpClient.get<InventoryResourceModel[]>('assets/mockData/iamDemoInventoryMockData.json').pipe(
          take(1),
          tap((m) => (this._iamDemoMockResourcesRaw = m))
        )
      );
    }

    combineLatest(loads)
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._prependAllMockResources(apiViewModels),
        error: () => {
          this.showNoData = apiViewModels.length === 0;
          this.inventoryResourcesCurrentPage$.next(apiViewModels);
          this.isLoading$.next(false);
        },
      });
  }

  private _filterMockResources(models: InventoryResourceModel[], filters: ISelectedFilters): InventoryResourceModel[] {
    return models.filter((m) => {
      if (filters.search && !m.displayName?.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }
      if (filters.aiService?.length && (!m.aiService || !filters.aiService.includes(m.aiService))) {
        return false;
      }
      if (filters.category?.length && !filters.category.includes(m.resourceCategory)) {
        return false;
      }
      if (filters.type?.length) {
        const resolvedTypes = filters.type.flatMap((t) => {
          const mapped = copilotToAgentTypeMapping.get(t as ResourceType);
          return mapped ? [t, mapped] : [t];
        });
        if (!resolvedTypes.includes(m.resourceType)) {
          return false;
        }
      }
      if (filters.resourceStatus?.length && !filters.resourceStatus.includes(m.resourceState?.status)) {
        return false;
      }
      if (filters.platform?.length && !filters.platform.includes(m.platformInfo?.platform)) {
        return false;
      }
      if (filters.integration?.length && !filters.integration.includes(m.integration?.id)) {
        return false;
      }
      if (
        filters.securityRisk?.length &&
        (!m.scores?.securityRisk || !filters.securityRisk.includes(m.scores.securityRisk.toString()))
      ) {
        return false;
      }
      if (
        filters.businessCriticality?.length &&
        (!m.scores?.businessCriticality ||
          !filters.businessCriticality.includes(m.scores.businessCriticality.toString()))
      ) {
        return false;
      }
      const envName = m.environment?.value?.displayName;
      if (filters.environment?.length && (!envName || !filters.environment.includes(envName))) {
        return false;
      }
      return true;
    });
  }

  private _prependAllMockResources(apiViewModels: IInventoryResourceViewModel[]): void {
    const filters = this.pageFilters.getSelectedFilters();
    let allMock: IInventoryResourceViewModel[] = [];

    if (this._isIamDemoEnabled() && this._iamDemoMockResourcesRaw) {
      const filtered = this._filterMockResources(this._iamDemoMockResourcesRaw, filters);
      const vms = filtered.map((m) => this._transformResourceToViewModel(m));
      this._iamDemoMockCount = vms.length;
      allMock = [...allMock, ...vms];
    }

    if (this._isRsaDemoEnabled() && this._rsaMockResourcesRaw) {
      const filtered = this._filterMockResources(this._rsaMockResourcesRaw, filters);
      const vms = filtered.map((m) => this._transformResourceToViewModel(m));
      this._rsaMockCount = vms.length;
      allMock = [...allMock, ...vms];
    }

    const combined = [...allMock, ...apiViewModels];
    this.showNoData = combined.length === 0;
    this.inventoryResourcesCurrentPage$.next(combined);
    this.isLoading$.next(false);
  }
}
